import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { supabase } from '@/integrations/supabase/client';

export interface WishlistItem {
  handle: string;
  title?: string;
  image?: string;
  price?: number;
  currencyCode?: string;
  addedAt: number;
}

interface WishlistState {
  items: WishlistItem[];
  /** Derived Set of handles for O(1) selectors — kept in sync on every mutation. */
  handles: Set<string>;
  loaded: boolean;
  has: (handle: string) => boolean;
  /** Toggle wishlist. Returns the new state (true = added, false = removed). */
  toggle: (item: WishlistItem, userId?: string | null) => Promise<boolean>;
  remove: (handle: string, userId?: string | null) => Promise<void>;
  /** Hydrate from DB when a user signs in. Also migrates any anonymous items. */
  hydrateFromDb: (userId: string) => Promise<void>;
  /** Clear local state on sign-out. */
  clearLocal: () => void;
}

export const useWishlist = create<WishlistState>()(
  persist(
    (set, get) => ({
      items: [],
      handles: new Set<string>(),
      loaded: false,

      has: (handle) => get().handles.has(handle),

      toggle: async (item, userId) => {
        const exists = get().has(item.handle);
        if (exists) {
          set((s) => {
            const handles = new Set(s.handles);
            handles.delete(item.handle);
            return { items: s.items.filter((i) => i.handle !== item.handle), handles };
          });
          if (userId) {
            await supabase
              .from('wishlists')
              .delete()
              .eq('user_id', userId)
              .eq('shopify_handle', item.handle);
          }
          return false;
        }
        const next: WishlistItem = { ...item, addedAt: Date.now() };
        set((s) => {
          const handles = new Set(s.handles);
          handles.add(item.handle);
          return { items: [next, ...s.items], handles };
        });
        if (userId) {
          await supabase
            .from('wishlists')
            .upsert(
              { user_id: userId, shopify_handle: item.handle },
              { onConflict: 'user_id,shopify_handle' },
            );
        }
        return true;
      },

      remove: async (handle, userId) => {
        set((s) => {
          const handles = new Set(s.handles);
          handles.delete(handle);
          return { items: s.items.filter((i) => i.handle !== handle), handles };
        });
        if (userId) {
          await supabase
            .from('wishlists')
            .delete()
            .eq('user_id', userId)
            .eq('shopify_handle', handle);
        }
      },

      hydrateFromDb: async (userId) => {
        // 1. Push any anonymous local items into DB (one-time migration).
        const local = get().items;
        if (local.length > 0) {
          await supabase.from('wishlists').upsert(
            local.map((i) => ({
              user_id: userId,
              shopify_handle: i.handle,
            })),
            { onConflict: 'user_id,shopify_handle' },
          );
        }

        // 2. Pull authoritative list from DB.
        const { data } = await supabase
          .from('wishlists')
          .select('shopify_handle, added_at')
          .eq('user_id', userId)
          .order('added_at', { ascending: false });

        if (data) {
          // Merge with local metadata (title/image/price) where we have it.
          const byHandle = new Map(local.map((i) => [i.handle, i]));
          const items = data.map((row) => {
            const meta = byHandle.get(row.shopify_handle);
            return {
              handle: row.shopify_handle,
              title: meta?.title,
              image: meta?.image,
              price: meta?.price,
              currencyCode: meta?.currencyCode,
              addedAt: new Date(row.added_at).getTime(),
            };
          });
          set({
            items,
            handles: new Set(items.map((i) => i.handle)),
            loaded: true,
          });
        } else {
          set({ loaded: true });
        }
      },

      clearLocal: () => set({ items: [], handles: new Set(), loaded: false }),
    }),
    {
      name: 'bloom-wishlist',
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({ items: s.items }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.handles = new Set(state.items.map((i) => i.handle));
        }
      },
    },
  ),
);
